import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Search, Loader2, UserPlus, AlertTriangle, Download, Megaphone } from 'lucide-react';
import { useUiLanguage } from '@/lib/i18n';
import { AI_ACTION_TYPES, executeAIAction } from '@/lib/aiActionEngine';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';
import {
  apiMerchantDashboard,
  apiMerchantCustomerActivity,
  apiMerchantAddCustomer,
} from '@/services/api';

const statusMeta = {
  vip_regular: { label: 'VIP Regular', className: 'bg-emerald-100 text-emerald-700' },
  steady_diner: { label: 'Steady Diner', className: 'bg-emerald-100 text-emerald-700' },
  cooling_down: { label: 'Cooling Down', className: 'bg-amber-100 text-amber-700' },
  missing_you: { label: 'Missing You', className: 'bg-rose-100 text-rose-700' },
  dormant: { label: 'Dormant', className: 'bg-stone-200 text-stone-700' },
  first_taste: { label: 'First Taste', className: 'bg-cyan-100 text-cyan-700' },
};

const HEALTH_COLORS = ['#22c55e', '#f59e0b', '#ef4444'];

function HealthTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 shadow-sm">
      <p className="text-xs font-semibold text-stone-700">{t(row.key)}</p>
      <p className="text-xs text-stone-500 mt-0.5">{row.value} {t('customers')} ({row.percent}%)</p>
    </div>
  );
}

function downloadCsv(filename, rows) {
  const body = rows.map((r) => r.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function daysUntilNextDate(rawDate) {
  if (!rawDate) return null;
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return null;
  const now = new Date();
  const next = new Date(now.getFullYear(), parsed.getMonth(), parsed.getDate());
  if (next < now) next.setFullYear(next.getFullYear() + 1);
  return Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function daysSinceVisit(customer) {
  const raw = Number(customer?.days_since_last_visit);
  if (Number.isFinite(raw)) return Math.max(raw, 0);
  const lastVisit = customer?.last_visit ? new Date(customer.last_visit) : null;
  if (!lastVisit || Number.isNaN(lastVisit.getTime())) return 999;
  return Math.max(Math.floor((Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24)), 0);
}

function humanizeLastVisit(customer, t) {
  const days = daysSinceVisit(customer);
  if (days <= 0) return t('Today');
  if (days === 1) return t('1 day ago');
  return t('{{n}} days ago').replace('{{n}}', String(days));
}

function rowActionMeta(customer, t) {
  const days = daysSinceVisit(customer);
  if (days < 14) {
    return {
      label: t('Reward'),
      subtype: 'high_frequency_reward',
      segment: 'ACTIVE',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    };
  }
  if (days <= 30) {
    return {
      label: t('Bring Back'),
      subtype: 'visit_booster',
      segment: 'AT_RISK',
      className: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
    };
  }
  return {
    label: t('Win Back'),
    subtype: 'win_back',
    segment: 'LOST',
    className: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
  };
}

function actionAudienceHint(segment, t) {
  if (segment === 'ACTIVE') return t('who may visit soon');
  if (segment === 'AT_RISK') return t('who may return soon');
  return t('ready for a comeback');
}

export default function CustomersPage() {
  const { t } = useUiLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedSideCustomerId, setSelectedSideCustomerId] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [toast, setToast] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const customers = useMemo(() => dashboard?.customers || [], [dashboard?.customers]);
  const enrichedCustomers = useMemo(
    () =>
      customers.map((c) => {
        const days = Number(c?.days_since_last_visit);
        const safeDays = Number.isFinite(days) ? Math.max(days, 0) : 999;
        let bucketKey = 'lost';
        if (safeDays < 14) bucketKey = 'active';
        else if (safeDays <= 30) bucketKey = 'risk';

        return {
          ...c,
          bucket: bucketKey,
          total_spend_with_rewards: Number((Number(c?.total_earned_points || 0) / 5).toFixed(2)),
          spend_by_points: Number((Number(c?.points || 0) / 100).toFixed(2)),
        };
      }),
    [customers]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = bucket === 'all' ? enrichedCustomers : enrichedCustomers.filter((c) => c.bucket === bucket);
    if (!q) return source;
    return source.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q));
  }, [bucket, enrichedCustomers, search]);

  const selectedSideCustomer = useMemo(
    () => enrichedCustomers.find((c) => c.id === selectedSideCustomerId) || null,
    [enrichedCustomers, selectedSideCustomerId]
  );

  const healthCounts = useMemo(() => {
    const result = { active: 0, risk: 0, lost: 0 };
    enrichedCustomers.forEach((c) => {
      if (c.bucket === 'active') result.active += 1;
      else if (c.bucket === 'risk') result.risk += 1;
      else result.lost += 1;
    });
    return result;
  }, [enrichedCustomers]);

  const healthData = useMemo(
    () => [
      {
        bucket: 'active',
        key: t('Active'),
        value: healthCounts.active,
        percent: enrichedCustomers.length ? Math.round((healthCounts.active / enrichedCustomers.length) * 100) : 0,
      },
      {
        bucket: 'risk',
        key: t('At Risk'),
        value: healthCounts.risk,
        percent: enrichedCustomers.length ? Math.round((healthCounts.risk / enrichedCustomers.length) * 100) : 0,
      },
      {
        bucket: 'lost',
        key: t('Lost'),
        value: healthCounts.lost,
        percent: enrichedCustomers.length ? Math.round((healthCounts.lost / enrichedCustomers.length) * 100) : 0,
      },
    ],
    [enrichedCustomers.length, healthCounts.active, healthCounts.lost, healthCounts.risk, t]
  );

  const bucketConfig = useMemo(
    () => [
      {
        key: 'active',
        label: t('Active'),
        count: healthCounts.active,
        className: 'border-emerald-200 text-emerald-700 bg-emerald-50',
        campaignSubtype: 'high_frequency_reward',
      },
      {
        key: 'risk',
        label: t('At Risk'),
        count: healthCounts.risk,
        className: 'border-amber-200 text-amber-700 bg-amber-50',
        campaignSubtype: 'visit_booster',
      },
      {
        key: 'lost',
        label: t('Lost'),
        count: healthCounts.lost,
        className: 'border-rose-200 text-rose-700 bg-rose-50',
        campaignSubtype: 'win_back',
      },
      {
        key: 'all',
        label: t('All'),
        count: enrichedCustomers.length,
        className: 'border-sky-200 text-sky-700 bg-sky-50',
        campaignSubtype: 'win_back',
      },
    ],
    [enrichedCustomers.length, healthCounts.active, healthCounts.lost, healthCounts.risk, t]
  );

  const activeBucket = bucketConfig.find((item) => item.key === bucket) || bucketConfig[3];

  const aiAssistConfig = useMemo(() => {
    const map = {
      active: {
        title: `${healthCounts.active} ${t('active customers need engagement boost.')}`,
        cta: t('Boost Engagement'),
        subtype: 'high_frequency_reward',
        segment: 'ACTIVE',
        audience: healthCounts.active,
      },
      risk: {
        title: `${healthCounts.risk} ${t('customers are at risk of churn.')}`,
        cta: t('Prevent Churn'),
        subtype: 'visit_booster',
        segment: 'AT_RISK',
        audience: healthCounts.risk,
      },
      lost: {
        title: `${healthCounts.lost} ${t('customers moved to Lost this week.')}`,
        cta: t('Run Win-back Campaign'),
        subtype: 'win_back',
        segment: 'LOST',
        audience: healthCounts.lost,
      },
      all: {
        title: `${healthCounts.risk + healthCounts.lost} ${t('customers need immediate follow-up.')}`,
        cta: t('Run Retention Campaign'),
        subtype: 'win_back',
        segment: 'ALL',
        audience: healthCounts.risk + healthCounts.lost,
      },
    };
    return map[bucket] || map.all;
  }, [bucket, healthCounts.active, healthCounts.lost, healthCounts.risk, t]);

  const aiAssistTone = useMemo(() => {
    const tones = {
      active: {
        card: 'border-emerald-200 bg-gradient-to-br from-emerald-100 via-white to-emerald-50',
        label: 'text-emerald-700',
        button: 'border-emerald-200 text-emerald-700 hover:bg-emerald-100',
      },
      risk: {
        card: 'border-amber-200 bg-gradient-to-br from-amber-100 via-white to-amber-50',
        label: 'text-amber-700',
        button: 'border-amber-200 text-amber-700 hover:bg-amber-100',
      },
      lost: {
        card: 'border-rose-200 bg-gradient-to-br from-rose-100 via-white to-rose-50',
        label: 'text-rose-700',
        button: 'border-rose-200 text-rose-700 hover:bg-rose-100',
      },
      all: {
        card: 'border-sky-200 bg-gradient-to-br from-sky-100 via-white to-sky-50',
        label: 'text-sky-700',
        button: 'border-sky-200 text-sky-700 hover:bg-sky-100',
      },
    };
    return tones[bucket] || tones.all;
  }, [bucket]);

  const setToastMessage = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3800);
  };

  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
      const d = await apiMerchantDashboard();
        setDashboard(d);
        if (!selectedSideCustomerId && d?.customers?.length) {
          setSelectedSideCustomerId(d.customers[0].id);
        }
      } catch (e) {}
      if (!silent) setLoading(false);
    },
    [selectedSideCustomerId]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => fetchData(true), 4000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const fetchCustomerDetails = useCallback(async (customerId, forModal = false) => {
    if (!customerId) return;
    if (forModal) setDetailsLoading(true);
    try {
      const activity = await apiMerchantCustomerActivity(customerId);
      if (forModal) setDetails(activity);
    } catch {
      if (forModal) setDetails(null);
    }
    if (forModal) setDetailsLoading(false);
  }, []);

  const openDetails = async (customerId) => {
    setSelectedSideCustomerId(customerId);
    setDetailsOpen(true);
    await fetchCustomerDetails(customerId, true);
  };

  const runAddCustomer = async () => {
    if (!newPhone.trim()) {
      setToastMessage(t('Phone number is required.'));
      return;
    }
    setBusyAction('add-customer');
    try {
      const res = await apiMerchantAddCustomer(newName.trim(), newPhone.trim());
      setNewName('');
      setNewPhone('');
      await fetchData(true);
      if (res?.already_exists) setToastMessage(t('Customer already exists for this restaurant.'));
      else setToastMessage(t('Customer added successfully.'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : t('Unable to add customer.'));
    }
    setBusyAction('');
  };

  const runBucketCampaign = async () => {
    if (aiBusy) return;
    const subtype = aiAssistConfig?.subtype;
    if (!subtype) return;
    setAiBusy(true);
    try {
      const res = await executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
        campaign_subtype: subtype,
        segment: aiAssistConfig?.segment,
        audience_count: aiAssistConfig?.audience || 0,
      });
      setToastMessage(`✅ ${res.message}`);
      fetchData(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Unable to run campaign right now.')}`);
    }
    setAiBusy(false);
  };

  const campaignSubtypeForCustomer = useCallback((customer) => {
    if (!customer) return 'win_back';
    const dobDays = daysUntilNextDate(customer.date_of_birth);
    if (dobDays !== null && dobDays <= 7) return 'birthday_campaign';

    const annDays = daysUntilNextDate(customer.anniversary_date);
    if (annDays !== null && annDays <= 7) return 'customer_anniversary';

    if (customer.bucket === 'active') return 'high_frequency_reward';
    if (customer.bucket === 'risk') return 'visit_booster';
    return 'win_back';
  }, []);

  const runCustomerAction = async (customer, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!customer?.id) return;
    const action = rowActionMeta(customer, t);
    const actionKey = `row-campaign-${customer.id}`;
    setBusyAction(actionKey);
    try {
      await executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
        type: 'AUTO',
        customerId: customer.id,
        campaign_subtype: action.subtype,
        segment: action.segment,
        audience_count: 1,
      });

      const cohortSize = enrichedCustomers.filter((row) => rowActionMeta(row, t).segment === action.segment).length;
      const additionalCount = Math.max(cohortSize - 1, 0);
      const customerName = String(customer?.name || t('this customer')).trim();
      const audienceHint = actionAudienceHint(action.segment, t);
      const additionalLabel = `${additionalCount} ${additionalCount === 1 ? t('customer') : t('customers')}`;
      const customMessage = additionalCount > 0
        ? `✅ ${t('Campaign sent to')} ${customerName} ${t('and')} ${additionalLabel} ${audienceHint}.⭐`
        : `✅ ${t('Campaign sent to')} ${customerName}.⭐`;

      setToastMessage(customMessage);
      fetchData(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Unable to run campaign right now.')}`);
    }
    setBusyAction('');
  };

  const exportCustomersCsv = () => {
    const rows = [
      ['Customer', 'Phone', 'Active Points', 'Lifetime Earned Points', 'Last Visit', 'Status'],
      ...filtered.map((c) => [
        c.name || '',
        c.phone || '',
        c.points || 0,
        c.total_earned_points || 0,
        c.last_visit ? new Date(c.last_visit).toISOString() : '',
        statusMeta[c.status]?.label || 'First Taste',
      ]),
    ];
    downloadCsv('flyback-customers.csv', rows);
  };

  const exportCustomerTransactionsCsv = () => {
    if (!details?.customer) return;
    const rows = [
      ['Customer', details.customer.name || ''],
      ['Phone', details.customer.phone || ''],
      [],
      ['Date', 'Type', 'Title', 'Detail', 'Points', 'Amount', 'Order ID'],
      ...(details.transactions || []).map((tx) => [
        tx.created_at ? new Date(tx.created_at).toISOString() : '',
        tx.type || '',
        tx.title || '',
        tx.detail || '',
        tx.points || 0,
        tx.amount || '',
        tx.order_id || '',
      ]),
    ];
    downloadCsv(`${String(details.customer.name || 'customer').replace(/\s+/g, '-').toLowerCase()}-transactions.csv`, rows);
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <CenteredStatusPopup message={toast} />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {t('Customers')}
          </h1>
          <p className="text-sm text-stone-500">{t('Segment, act fast, and retain more regular diners.')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {bucketConfig.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setBucket(item.key)}
              className={`h-9 px-3 rounded-lg border text-xs font-semibold transition-all ${item.className} ${bucket === item.key ? 'ring-2 ring-offset-1 ring-brand/30 shadow-sm' : 'opacity-80 hover:opacity-100'}`}
            >
              {item.label} ({item.count})
            </button>
          ))}
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Search')} className="h-9 rounded-xl pl-10 text-xs" data-testid="customer-search" />
          </div>
          <button onClick={() => setAddOpen(true)} className="h-9 px-3 rounded-lg gradient-brand-btn text-xs font-semibold flex items-center gap-1">
            <UserPlus className="w-3.5 h-3.5" /> + {t('Add')}
          </button>
          <button onClick={exportCustomersCsv} className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold flex items-center gap-1">
            <Download className="w-3.5 h-3.5" /> {t('Download CSV')}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="mx-auto w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
          <div className="rounded-xl border border-stone-200 bg-white p-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider text-center">{t('Customer Segments')}</p>
            <div className="relative h-56 mt-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={healthData}
                    dataKey="value"
                    nameKey="key"
                    innerRadius={48}
                    outerRadius={82}
                    strokeWidth={0}
                    onClick={(entry) => setBucket(entry?.bucket || 'all')}
                  >
                    {healthData.map((entry, idx) => {
                      const isActiveSlice = bucket === 'all' ? true : bucket === entry.bucket;
                      return (
                        <Cell
                          key={`segment-${entry.key}`}
                          fill={HEALTH_COLORS[idx % HEALTH_COLORS.length]}
                          fillOpacity={isActiveSlice ? 1 : 0.35}
                          stroke={isActiveSlice ? '#0f172a22' : 'transparent'}
                          strokeWidth={isActiveSlice ? 2 : 0}
                        />
                      );
                    })}
                  </Pie>
                  <RechartsTooltip content={<HealthTooltip t={t} />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{t('Total')}</p>
                <p className="text-3xl font-bold text-[#0f172a]">{customers.length.toLocaleString()}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10px] mt-1">
              <p className="font-semibold text-emerald-700 text-center">{t('Active')}: {healthCounts.active}</p>
              <p className="font-semibold text-amber-700 text-center">{t('At Risk')}: {healthCounts.risk}</p>
              <p className="font-semibold text-rose-700 text-center">{t('Lost')}: {healthCounts.lost}</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={bucket}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.99 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className={`rounded-xl border p-7 lg:p-8 text-center flex flex-col justify-center min-h-[248px] transition-colors duration-300 ${aiAssistTone.card}`}
            >
              <p className={`text-sm font-bold uppercase tracking-wider ${aiAssistTone.label}`}>{t('AI Assist')}</p>
              <p className="text-2xl lg:text-[1.95rem] font-bold text-[#0f172a] mt-3 leading-tight">{aiAssistConfig.title}</p>
              <button
                type="button"
                onClick={runBucketCampaign}
                disabled={aiBusy || !aiAssistConfig?.audience}
                className={`mt-6 h-12 px-7 rounded-xl bg-white border text-base font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 self-center transition-colors ${aiAssistTone.button}`}
              >
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                {t(aiAssistConfig.cta)}
              </button>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-stone-100 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">{t('Customer List')}</p>
            <p className="text-xs text-stone-500">{filtered.length} {t('customers')} {t('in')} {t(activeBucket.label)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-stone-100">
                  {['Customer Name', 'Last Visit', 'Total Visits', 'Total Spend with rewards', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-stone-400">{t(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        <td colSpan={5} className="px-4 py-4">
                          <div className="h-4 rounded bg-stone-100 animate-pulse" />
                        </td>
                      </tr>
                    ))
                  : filtered.length === 0
                  ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-stone-500">{t('No customers found.')}</td>
                    </tr>
                    )
                  : filtered.map((c) => (
                      (() => {
                        const action = rowActionMeta(c, t);
                        const rowBusy = busyAction === `row-campaign-${c.id}`;
                        return (
                      <tr
                        key={c.id}
                        className={`border-b border-stone-100 cursor-pointer ${
                          c.bucket === 'active'
                            ? 'bg-emerald-100 hover:bg-emerald-200/80'
                            : c.bucket === 'risk'
                            ? 'bg-amber-100 hover:bg-amber-200/80'
                            : 'bg-rose-100 hover:bg-rose-200/80'
                        } ${selectedSideCustomerId === c.id ? 'ring-1 ring-inset ring-brand/30' : ''}`}
                        onClick={() => openDetails(c.id)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold flex items-center gap-1.5">
                            {c.name}
                            {(() => {
                              const dobDays = daysUntilNextDate(c.date_of_birth);
                              const annDays = daysUntilNextDate(c.anniversary_date);
                              const upcoming = (dobDays !== null && dobDays <= 10) || (annDays !== null && annDays <= 10);
                              if (!upcoming) return null;
                              return (
                                <span className="relative group">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                                  <span className="pointer-events-none absolute left-5 top-0 w-56 rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-[10px] text-stone-600 shadow-lg opacity-0 group-hover:opacity-100">
                                    {t('Upcoming event')}:{' '}
                                    {dobDays !== null && dobDays <= 10 ? `${t('Birthday in')} ${dobDays} ${t('day(s)')}` : ''}
                                    {dobDays !== null && dobDays <= 10 && annDays !== null && annDays <= 10 ? ' • ' : ''}
                                    {annDays !== null && annDays <= 10 ? `${t('Anniversary in')} ${annDays} ${t('day(s)')}` : ''}
                                  </span>
                                </span>
                              );
                            })()}
                          </p>
                          <p className="text-xs text-stone-500">{String(c.phone || '')}</p>
                        </td>
                        <td className="px-4 py-3 font-semibold text-stone-700">{humanizeLastVisit(c, t)}</td>
                        <td className="px-4 py-3 font-semibold">{Number(c.visits || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 font-semibold">${Number(c.total_spend_with_rewards || 0).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={(e) => runCustomerAction(c, e)}
                            disabled={rowBusy}
                            className={`h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 ${action.className}`}
                            data-testid={`customers-action-${c.id}`}
                          >
                            {rowBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                            {action.label}
                          </button>
                        </td>
                      </tr>
                        );
                      })()
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogTitle className="text-lg font-bold">{t('Add Customer')}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{t('Create a customer profile for quick loyalty enrollment.')}</DialogDescription>
          <div className="space-y-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('Customer name')} className="h-10 rounded-xl" />
            <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder={t('Phone number')} className="h-10 rounded-xl" />
          </div>
          <button
            onClick={async () => {
              await runAddCustomer();
              setAddOpen(false);
            }}
            disabled={busyAction === 'add-customer'}
            className="h-10 rounded-xl gradient-brand-btn text-sm font-semibold flex items-center justify-center gap-2"
          >
            {busyAction === 'add-customer' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> {t('Add Customer')}</>}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="w-[96vw] max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="customer-details-modal">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-bold">{t('Customer Activity')}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">{t('Detailed points, transactions, and visit history.')}</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  const fullCustomer = enrichedCustomers.find((row) => row.id === details?.customer?.id) || selectedSideCustomer;
                  const subtype = campaignSubtypeForCustomer(fullCustomer);
                  setBusyAction('details-campaign');
                  try {
                    const res = await executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
                      campaign_subtype: subtype,
                      segment: fullCustomer?.bucket === 'active' ? 'ACTIVE' : fullCustomer?.bucket === 'risk' ? 'AT_RISK' : 'LOST',
                      audience_count: 1,
                    });
                    setToastMessage(`✅ ${res.message}`);
                  } catch (e) {
                    const detail = e?.response?.data?.detail;
                    setToastMessage(`❌ ${typeof detail === 'string' ? detail : t('Unable to run campaign right now.')}`);
                  }
                  setBusyAction('');
                }}
                disabled={busyAction === 'details-campaign'}
                className="h-9 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
              >
                {busyAction === 'details-campaign' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t('Run Campaign')}
              </button>
              <button
                type="button"
                onClick={exportCustomerTransactionsCsv}
                disabled={!details?.transactions?.length}
                className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold disabled:opacity-50"
              >
                {t('Export Transactions CSV')}
              </button>
            </div>
          </div>

          {detailsLoading && <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!detailsLoading && !details && <p className="text-sm text-muted-foreground py-6">{t('Unable to load customer details.')}</p>}
          {!detailsLoading && details && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">{t('Name')}</p><p className="text-sm font-semibold mt-1">{details.customer.name}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">{t('Balance')}</p><p className="text-sm font-semibold mt-1">{details.summary.points_balance.toLocaleString()} {t('pts')}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">{t('Points Earned')}</p><p className="text-sm font-semibold mt-1 text-emerald-600">{details.summary.points_earned.toLocaleString()}</p></div>
                <div className="rounded-xl border border-border p-3"><p className="text-[11px] text-muted-foreground">{t('Visits')}</p><p className="text-sm font-semibold mt-1">{details.summary.visits}</p></div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold mb-2">{t('Visit History')}</p>
                <div className="max-h-32 overflow-auto space-y-1">
                  {(details.visit_history || []).slice(0, 15).map((v, idx) => (
                    <div key={`${v.date}-${idx}`} className="text-xs text-muted-foreground flex justify-between">
                      <span>{v.date ? new Date(v.date).toLocaleString() : t('N/A')}</span>
                      <span>{v.order_id ? `${t('Order')} #${v.order_id}` : t('Visit')} • +{v.points} {t('pts')}</span>
                    </div>
                  ))}
                  {(details.visit_history || []).length === 0 && <p className="text-xs text-muted-foreground">{t('No visits yet.')}</p>}
                </div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold mb-2">{t('Recent Transactions')}</p>
                <div className="max-h-60 overflow-auto divide-y divide-border">
                  {(details.transactions || []).slice(0, 20).map((row) => (
                    <div key={row.id} className="py-2.5 text-xs flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{row.title}</p>
                        {row.detail && <p className="text-muted-foreground mt-0.5 text-xs whitespace-pre-wrap break-words">{row.detail}</p>}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`font-semibold ${row.points >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{row.points >= 0 ? '+' : ''}{row.points} {t('pts')}</p>
                        <p className="text-muted-foreground">{row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
